import { useState, useEffect, useRef, useCallback, useDeferredValue } from "react";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import PlayerListManager from "../../managers/PlayerListManager";
import { getErrorMessage } from "../../utils/errorUtils";
import { fetchAPI } from "../../config/api";
import { getDataTypeConfig } from "../../config/dataControlsConfig";
import { DEFAULT_YEAR, DEFAULT_DIVISION } from "../../config/constants";
import { DEFAULT_MIN_PA, DEFAULT_MIN_IP, DEFAULT_MIN_BF, getEndpoint } from "./constants";

export const useLeaderboardData = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialYears = () => {
    const yearsParam = searchParams.get("years");
    if (yearsParam) {
      const years = yearsParam.split(",").map(Number).filter((y) => !Number.isNaN(y));
      if (years.length) return years;
    }
    const single = Number(searchParams.get("year"));
    return !Number.isNaN(single) && single ? [single] : [DEFAULT_YEAR];
  };

  const [leaderboardType, setLeaderboardType] = useState(searchParams.get("type") || "value");
  const [selectedYears, setSelectedYears] = useState(getInitialYears);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [minPA, setMinPA] = useState(Number(searchParams.get("minPA")) || DEFAULT_MIN_PA);
  const [minIP, setMinIP] = useState(Number(searchParams.get("minIP")) || DEFAULT_MIN_IP);
  const [minBF, setMinBF] = useState(Number(searchParams.get("minBF")) || DEFAULT_MIN_BF);
  const [division, setDivision] = useState(Number(searchParams.get("division")) || DEFAULT_DIVISION);
  const [conference, setConference] = useState(searchParams.get("conference") || "");
  const [listId, setListId] = useState(searchParams.get("listId") || "");
  const [playerType, setPlayerType] = useState(searchParams.get("pt") === "pitcher" ? "pitcher" : "batter");
  const [viewLevel, setViewLevel] = useState(searchParams.get("vl") === "team" ? "team" : "player");

  const deferredSearch = useDeferredValue(searchTerm);

  const [data, setData] = useState([]);
  const [conferences, setConferences] = useState([]);
  const [listPlayerIds, setListPlayerIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showError, setShowError] = useState(false);

  const dataAbortRef = useRef(null);
  const conferencesAbortRef = useRef(null);
  const mounted = useRef(false);

  const updateParamsRef = useRef(null);
  if (!updateParamsRef.current) {
    updateParamsRef.current = debounce((state) => {
      const params = new URLSearchParams();
      params.set("type", state.leaderboardType);
      params.set("years", state.selectedYears.join(","));
      params.set("search", state.searchTerm);
      params.set("minPA", state.minPA.toString());
      params.set("minIP", state.minIP.toString());
      params.set("minBF", state.minBF.toString());
      params.set("division", state.division.toString());
      if (state.conference) params.set("conference", state.conference);
      if (state.listId) params.set("listId", state.listId);
      if (state.playerType) params.set("pt", state.playerType);
      if (state.viewLevel) params.set("vl", state.viewLevel);
      setSearchParams(params);
    }, 300);
  }

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    updateParamsRef.current({ leaderboardType, selectedYears, searchTerm, minPA, minIP, minBF, division, conference, listId, playerType, viewLevel });
  }, [leaderboardType, selectedYears, searchTerm, minPA, minIP, minBF, division, conference, listId, playerType, viewLevel]);

  const fetchPlayerList = useCallback(async () => {
    if (!listId) { setListPlayerIds([]); return; }
    try {
      setLoadingList(true);
      const list = await PlayerListManager.getPlayerListById(listId);
      setListPlayerIds(list?.playerIds || []);
    } catch { setListPlayerIds([]); }
    finally { setLoadingList(false); }
  }, [listId]);

  useEffect(() => { fetchPlayerList(); }, [listId, fetchPlayerList]);

  const fetchConferences = useCallback(async () => {
    conferencesAbortRef.current?.abort();
    const controller = new AbortController();
    conferencesAbortRef.current = controller;
    try {
      const resp = await fetchAPI(`/conferences?division=${division}&years=${selectedYears.join(",")}`, { signal: controller.signal });
      if (!controller.signal.aborted) {
        const confs = (Array.isArray(resp) ? resp : []).map((c) =>
          typeof c === "string" ? c : c?.conference || c?.name || String(c)
        );
        setConferences([...new Set(confs)].sort());
        setIsInitialized(true);
      }
    } catch (err) {
      if (err.name !== "AbortError" && !controller.signal.aborted) {
        setIsInitialized(true);
      }
    }
  }, [division, selectedYears]);

  useEffect(() => { fetchConferences(); }, [division, selectedYears, fetchConferences]);

  const fetchData = useCallback(async () => {
    if (!selectedYears.length && leaderboardType !== "rolling") return;

    dataAbortRef.current?.abort();
    const controller = new AbortController();
    dataAbortRef.current = controller;

    setLoading(true);
    setError(null);
    setShowError(false);

    try {
      const params = new URLSearchParams();
      params.set("division", division.toString());

      if (leaderboardType === "rolling") {
        params.set("window", "25");
        params.set("sort_order", "desc");
        params.set("player_type", playerType);
      } else {
        if (selectedYears.length > 0) {
          const sorted = [...selectedYears].sort();
          params.set("start_year", sorted[0].toString());
          params.set("end_year", sorted[sorted.length - 1].toString());
        }

        const cfg = getDataTypeConfig(leaderboardType);
        const filters = cfg.minFilters;
        if (filters) {
          const active = filters.all || filters[playerType];
          if (active) {
            let val, paramName;
            if (active.key === "minPA") {
              val = minPA;
              paramName = leaderboardType === "battedball" ? "min_bb" : "min_pa";
            } else if (active.key === "minBF") {
              val = minBF;
              paramName = "min_bf";
            } else {
              val = minIP;
              paramName = "min_ip";
            }
            params.set(paramName, Math.max(val, 0).toString());
          }
        }
      }

      const endpoint = getEndpoint(leaderboardType, playerType, viewLevel);
      const result = await fetchAPI(`${endpoint}?${params}`, { signal: controller.signal });
      const rows = Array.isArray(result) ? result : [];

      if (!controller.signal.aborted) {
        setData(rows.map((row, i) => ({ ...row, rank: i + 1 })));
        setError(null);
        setShowError(false);
      }
    } catch (err) {
      if (err.name !== "AbortError" && !controller.signal.aborted) {
        setError(getErrorMessage(err, { division, leaderboardType }));
        setShowError(true);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [selectedYears, division, leaderboardType, minIP, minBF, minPA, playerType, viewLevel]);

  useEffect(() => {
    if (!mounted.current || !isInitialized) return;
    fetchData();
  }, [selectedYears, division, leaderboardType, minPA, isInitialized, fetchData]);

  useEffect(() => () => {
    dataAbortRef.current?.abort();
    conferencesAbortRef.current?.abort();
    updateParamsRef.current?.cancel();
  }, []);

  const getFilteredData = useCallback(() => {
    const s = deferredSearch.toLowerCase();
    return data.filter((item) => {
      if (!item || typeof item !== "object") return false;

      const name = (item.player_name || item.team_name || "").toLowerCase();
      const team = (item.team_name || "").toLowerCase();

      if (conference && item.conference !== conference) return false;
      if (s && !name.includes(s) && !team.includes(s)) return false;

      if (listId && listPlayerIds.length > 0) {
        const pid = item.player_id;
        return pid && listPlayerIds.includes(pid.toString());
      }

      return true;
    });
  }, [data, deferredSearch, conference, listId, listPlayerIds]);

  return {
    leaderboardType, setLeaderboardType,
    selectedYears, setSelectedYears,
    searchTerm, setSearchTerm,
    minPA, setMinPA,
    minIP, setMinIP,
    minBF, setMinBF,
    division, setDivision,
    conference, setConference,
    listId, setListId,
    playerType, setPlayerType,
    viewLevel, setViewLevel,
    conferences,
    loading: loading || loadingList,
    isInitialized,
    error,
    showError,
    filteredData: getFilteredData(),
    listPlayerIds,
    fetchData,
  };
};
